import { PRODUCT_ASPECT_TYPES } from '@/constants/positions'
import { useProductStore } from '@/stores/product'
import type { Product, ProductWithInfo } from '@/types/product'

type ProductAspect = keyof typeof PRODUCT_ASPECT_TYPES
type ProductBuckets = Record<ProductAspect, ProductWithInfo[]>

// Reverse map to convert 'Square' → 'SQUARE'
const aspectValueToKey = Object.entries(PRODUCT_ASPECT_TYPES).reduce(
  (acc, [key, val]) => {
    acc[val] = key as ProductAspect
    return acc
  },
  {} as Record<string, ProductAspect>,
)

const goodCombinations: ProductAspect[][] = [
  // 2-product combinations
  ['BOX_VERTICAL', 'BOX_VERTICAL'],
  ['BOX_VERTICAL', 'BOX_HORIZONTAL'],
  ['BOX_HORIZONTAL', 'BOX_HORIZONTAL'],
  ['BOX_HORIZONTAL', 'SQUARE'],
  ['BOX_HORIZONTAL', 'VERTICAL'],
  ['VERTICAL', 'VERTICAL'],
  ['BOX_HORIZONTAL', 'HORIZONTAL'],
  ['HORIZONTAL', 'HORIZONTAL'],

  // 3-product combinations
  ['BOX_HORIZONTAL', 'HORIZONTAL', 'HORIZONTAL'],
  ['BOX_VERTICAL', 'HORIZONTAL', 'HORIZONTAL'],
  ['SQUARE', 'HORIZONTAL', 'HORIZONTAL'],
  ['BOX_VERTICAL', 'BOX_HORIZONTAL', 'HORIZONTAL'],
  ['BOX_VERTICAL', 'SQUARE', 'HORIZONTAL'],
  ['BOX_HORIZONTAL', 'SQUARE', 'HORIZONTAL'],
  ['SQUARE', 'HORIZONTAL', 'HORIZONTAL'],
  ['VERTICAL', 'VERTICAL', 'VERTICAL'],
  ['BOX_VERTICAL', 'BOX_VERTICAL', 'BOX_VERTICAL'],
  ['HORIZONTAL', 'HORIZONTAL', 'HORIZONTAL'],

  // 1-product fallback
  ['BOX_VERTICAL'],
  ['BOX_HORIZONTAL'],
  ['SQUARE'],
  ['HORIZONTAL'],
  ['VERTICAL'],
]

function bucketProducts(products: ProductWithInfo[]): ProductBuckets {
  const buckets: ProductBuckets = {
    BOX_VERTICAL: [],
    BOX_HORIZONTAL: [],
    SQUARE: [],
    HORIZONTAL: [],
    VERTICAL: [],
  }

  for (const product of products) {
    const aspectValue = product.productInfo.productData.aspectType
    if (!aspectValue) {
      console.warn('Product aspect type is missing for product:', product)
      continue
    }
    const aspectKey = aspectValueToKey[aspectValue]

    if (aspectKey && buckets[aspectKey]) {
      buckets[aspectKey].push(product)
    }
  }

  return buckets
}

function filterPossibleGoodCombinations(products: ProductBuckets): ProductAspect[][] {
  return goodCombinations.filter((combo) => {
    const countMap: Record<ProductAspect, number> = {} as any
    for (const aspect of combo) {
      countMap[aspect] = (countMap[aspect] || 0) + 1
    }
    return Object.entries(countMap).every(([aspect, count]) => {
      return (products[aspect as ProductAspect]?.length || 0) >= count
    })
  })
}

export function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

export function generateAllGoodCombinations(): string[][] {
  const productStore = useProductStore()
  const productsBucket = bucketProducts(productStore.selectedProducts as ProductWithInfo[])

  const possibleCombos = filterPossibleGoodCombinations(productsBucket)

  const allCombinations = new Set<string>()
  const finalResults: string[][] = []

  for (const combo of possibleCombos) {
    const aspectToProducts = Object.fromEntries(
      Object.entries(productsBucket).map(([k, v]) => [k, [...v]]),
    ) as ProductBuckets

    function backtrack(i: number, path: string[], used: Set<string>, localBuckets: ProductBuckets) {
      if (i === combo.length) {
        const key = [...path].sort().join(',')
        if (!allCombinations.has(key)) {
          allCombinations.add(key)
          finalResults.push([...path])
        }
        return
      }

      const aspect = combo[i]
      const list = localBuckets[aspect]
      for (let j = 0; j < list.length; j++) {
        const id = list[j].productInfo.productData.id
        if (used.has(id)) continue

        used.add(id)
        backtrack(i + 1, [...path, id], used, localBuckets)
        used.delete(id)
      }
    }

    backtrack(0, [], new Set(), aspectToProducts)
  }

  const shuffledRes = shuffle(finalResults)

  function reorderByPattern() {
    // Group arrays by their lengths
    const groupMap: { [key: string]: string[][] } = {
      '3': shuffledRes.filter((arr) => arr.length === 3),
      '2': shuffledRes.filter((arr) => arr.length === 2),
      '1': shuffledRes.filter((arr) => arr.length === 1),
    }

    const result: string[][] = []
    const order = ['3', '2', '1']

    while (groupMap['3'].length || groupMap['2'].length || groupMap['1'].length) {
      for (const len of order) {
        if (groupMap[len].length > 0) {
          result.push(groupMap[len].shift()!)
        }
      }
    }

    return result
  }

  return reorderByPattern()
}

export const isAllFoodCategories = (products: Product[]) => {
  const foodCategories = [
    'Cereals and Breakfast',
    'Cold Drinks and Juices',
    'Munchies And Snacks',
    'Instant Food and Frozen Food',
    'Tea, Coffee and More',
    'Chocolates',
    'Biscuits and Cakes',
    'Sauces and Spreads',
    'Dairy, Bread and Eggs',
    'Edible Oils and Ghee',
    'Masala, Pickle and Papad',
    'Atta, Rice and Dals',
    'Drinks',
    'Chips',
    'Baby',
    'Pasta & Noodles',
    'Specialty Fruit',
    'Frozen Foods',
  ]
  return products.every((product) => foodCategories.includes(product.parentCategory))
}
